import { z } from 'zod';

import { LINK_CODE_CONFIG } from './linkCodeConfig.js';

/**
 * Канал-кодирование `start_param` / `startapp` (Веха 8, ARCHITECTURE_PLAN §12,
 * CANON E2/G1/G3). Расширено work-035 (ADR 0014) короткими кодами — см.
 * `parseLinkParam`/`buildBoxLinkParam`/`buildAdLinkParam` ниже.
 *
 * ЕДИНЫЙ источник истины кодирования start_param в deep-link. Используется:
 *   - `@veylme/api` (`lib/deepLink.ts`, `services/preparedMessage.ts`) — сборка
 *     ссылок (короткий код + канал, `buildBoxLinkParam`);
 *   - `@veylme/miniapp` (`lib/tg.ts`) — парсинг входящего start_param
 *     (`parseLinkParam`);
 *   - `@veylme/bot` (`handlers/start.ts`, `links.ts`) — парсинг `/start <payload>`
 *     (`parseLinkParam`).
 *
 * LEGACY-префиксы `parseStartParam`/`buildStartParam` (сверены с
 * core.telegram.org/api/links — `start`/`startapp` ≤64 base64url, work/008
 * шапка, 2026-05-29):
 *   - `boxs_<token>` → channel = 'story'  (шар в Story);
 *   - `boxc_<token>` → channel = 'chat'   (prepared inline-message в чат);
 *   - `box_<token>`  → channel = 'direct' (copy-link / бот / прочее).
 *
 * BACKWARD-COMPATIBLE: `box_`/`boxs_`/`boxc_` + полный token остаются валидными
 * НАВСЕГДА (уже расшаренные ссылки не умирают, work-035 §9 DoD 2) — `parseLinkParam`
 * проверяет их ПЕРВЫМИ. Невалидный/неизвестный формат → `null` (caller →
 * graceful fallback на INBOX/онбординг, CANON G3 — не падаем).
 *
 * Pure: ни env, ни побочных эффектов — модуль безопасно импортируется и в Node
 * (api/bot), и в браузере (miniapp через Vite).
 */

/** Канал прихода реферала. Зеркало строкового поля `LoopEvent.channel`/`Referral.channel`. */
export type ReferralChannel = 'story' | 'chat' | 'direct';

/** Zod-enum канала — strict-валидация на границе (`RecordEventBody`, `CreateBoxBody.ref`). */
export const ReferralChannelEnum = z.enum(['story', 'chat', 'direct']);

// Порядок проверки важен: `boxs_`/`boxc_` — более специфичные префиксы, ДОЛЖНЫ
// проверяться РАНЬШЕ `box_` (иначе `box_` съел бы `boxs_…`/`boxc_…` как direct).
const STORY_PREFIX = 'boxs_';
const CHAT_PREFIX = 'boxc_';
const DIRECT_PREFIX = 'box_';

/**
 * Собрать `start_param`-значение для бокса+канала.
 *
 * `buildStartParam('abc', 'story')` → `'boxs_abc'`.
 * Не валидирует длину итога — caller обязан гарантировать ≤64 (token ≤ 64, токены
 * проекта = 22 base64url-символа → итог ≤ 27, запас есть).
 */
export function buildStartParam(token: string, channel: ReferralChannel): string {
  switch (channel) {
    case 'story':
      return `${STORY_PREFIX}${token}`;
    case 'chat':
      return `${CHAT_PREFIX}${token}`;
    case 'direct':
      return `${DIRECT_PREFIX}${token}`;
  }
}

/**
 * Разобрать сырой `start_param` / `startapp` в `{ token, channel }`.
 *
 * ⚠️ LEGACY-ONLY (work-035): распознаёт ТОЛЬКО старый формат `box_`/`boxs_`/
 * `boxc_` + полный token. Используется ВНУТРИ `parseLinkParam` (legacy-ветка,
 * см. ниже) — прямые вызовы вне этого модуля должны переходить на
 * `parseLinkParam`, который дополнительно понимает короткие коды (`b<s|c|d><code>`)
 * и рекламные метки (`a<code>`). Оставлен экспортированным ради обратной
 * совместимости тестов/build→parse round-trip и как единственный источник
 * разбора legacy-префиксов.
 *
 * Возвращает `null`, если:
 *   - значение не строка / не начинается ни с одного известного префикса;
 *   - токен после префикса пустой.
 *
 * Caller трактует `null` как «нет валидного реферала» → INBOX/онбординг (G3).
 */
export function parseStartParam(
  raw: unknown,
): { token: string; channel: ReferralChannel } | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;

  // Specific-first: story/chat literal, затем direct (см. комментарий к префиксам).
  if (raw.startsWith(STORY_PREFIX)) {
    const token = raw.slice(STORY_PREFIX.length);
    return token.length > 0 ? { token, channel: 'story' } : null;
  }
  if (raw.startsWith(CHAT_PREFIX)) {
    const token = raw.slice(CHAT_PREFIX.length);
    return token.length > 0 ? { token, channel: 'chat' } : null;
  }
  if (raw.startsWith(DIRECT_PREFIX)) {
    const token = raw.slice(DIRECT_PREFIX.length);
    return token.length > 0 ? { token, channel: 'direct' } : null;
  }
  return null;
}

/* ─────────────────────────────────────────────────────────────────────────
 * work-035 — короткие ссылки + рекламная атрибуция (ADR 0014, CANON E2/G1/G3).
 *
 * Новый компактный формат `<тип><канал/лит><код>` слитно (base62 `[0-9A-Za-z]`,
 * длина кода — константа `LINK_CODE_CONFIG.length` в `linkCodeConfig.ts`,
 * ЕДИНСТВЕННЫЙ источник истины длины — регексы ниже собраны из неё
 * динамически, хардкода длины здесь НЕТ):
 *   - `b` + `s|c|d` + `<code>` → бокс: `bs7k2Qm` (story/chat/direct);
 *   - `a` + `<code>`          → рекламная метка: `aK9m2x`.
 *
 * ЯКОРНЫЕ регексы (не `slice`!) — короткий код длиной РОВНО
 * `LINK_CODE_CONFIG.length` символов, лишний хвост/мусор не проходит молча.
 *
 * ПОРЯДОК: LEGACY ПЕРВЫМ (`parseStartParam` выше), потом новый формат.
 * Legacy всегда `b`+`o` (`box_`/`boxs_`/`boxc_`) — `o ∉ {s,c,d}`, ложных
 * срабатываний с новым форматом нет; legacy-first дополнительно страхует от
 * будущего канала на букву `o`.
 * ──────────────────────────────────────────────────────────────────────── */

const BOX_LINK_PREFIX = 'b';
const AD_LINK_PREFIX = 'a';

/** Буква канала в коротком box-формате (`b<letter><code>`). */
const CHANNEL_TO_LETTER: Record<ReferralChannel, 's' | 'c' | 'd'> = {
  story: 's',
  chat: 'c',
  direct: 'd',
};
const LETTER_TO_CHANNEL: Record<'s' | 'c' | 'd', ReferralChannel> = {
  s: 'story',
  c: 'chat',
  d: 'direct',
};

// Якорные regex — код РОВНО LINK_CODE_CONFIG.length base62-символов, ничего
// больше не проходит. Длина выведена из конфига (не хардкод) — если
// LINK_CODE_CONFIG.length изменится, парсер синхронно поедет за генератором
// кодов (apps/api|bot/src/lib/linkCodeGen.ts), рассинхрон невозможен.
const BOX_LINK_RE = new RegExp(`^b([scd])([0-9A-Za-z]{${LINK_CODE_CONFIG.length}})$`);
const AD_LINK_RE = new RegExp(`^a([0-9A-Za-z]{${LINK_CODE_CONFIG.length}})$`);

/**
 * Собрать короткий box-параметр: `buildBoxLinkParam('7k2Qm', 'story')` → `'bs7k2Qm'`.
 * Не валидирует длину `code` — caller (сервер, `ensureLinkCode`) гарантирует
 * `LINK_CODE_CONFIG.length` символов base62.
 */
export function buildBoxLinkParam(code: string, channel: ReferralChannel): string {
  return `${BOX_LINK_PREFIX}${CHANNEL_TO_LETTER[channel]}${code}`;
}

/** Собрать рекламный параметр: `buildAdLinkParam('K9m2x')` → `'aK9m2x'`. */
export function buildAdLinkParam(code: string): string {
  return `${AD_LINK_PREFIX}${code}`;
}

/**
 * Результат разбора `start_param` (work-035) — union по способу входа:
 *   - `{ kind:'box', code, channel }` — новый короткий код, требует резолва
 *     код→token через `GET /boxes/link/:code` (caller меняет код на полный
 *     token ОДИН раз на bootstrap, дальше ASK-поток идёт с token как раньше);
 *   - `{ kind:'box', token, channel }` — LEGACY (`box_`/`boxs_`/`boxc_`),
 *     token уже полный, резолв не нужен;
 *   - `{ kind:'ad', code }` — рекламная метка (`a<code>`), НЕ ask-контекст;
 *   - `null` — мусор/пусто → INBOX/онбординг (CANON G3).
 */
export type ParsedLinkParam =
  | { kind: 'box'; code: string; channel: ReferralChannel }
  | { kind: 'box'; token: string; channel: ReferralChannel }
  | { kind: 'ad'; code: string }
  | null;

/**
 * Разобрать сырой `start_param` / `startapp` (work-035, ЕДИНАЯ точка входа —
 * заменяет прямые вызовы `parseStartParam` везде, кроме внутренней legacy-ветки).
 *
 * ПОРЯДОК ПРОВЕРОК (инвариант, не переставлять): LEGACY ПЕРВЫМ → новый box-формат
 * → ad-формат → `null`. Anti-oracle/graceful: любой мусор → `null` (CANON G3),
 * никогда не бросает.
 */
export function parseLinkParam(raw: unknown): ParsedLinkParam {
  if (typeof raw !== 'string' || raw.length === 0) return null;

  // 1. LEGACY ПЕРВЫМ — box_/boxs_/boxc_ + полный token.
  const legacy = parseStartParam(raw);
  if (legacy) return { kind: 'box', token: legacy.token, channel: legacy.channel };

  // 2. Новый короткий box-формат: b<s|c|d><code>.
  const boxMatch = BOX_LINK_RE.exec(raw);
  if (boxMatch) {
    const letter = boxMatch[1] as 's' | 'c' | 'd';
    const code = boxMatch[2] as string;
    return { kind: 'box', code, channel: LETTER_TO_CHANNEL[letter] };
  }

  // 3. Рекламная метка: a<code>.
  const adMatch = AD_LINK_RE.exec(raw);
  if (adMatch) {
    return { kind: 'ad', code: adMatch[1] as string };
  }

  return null;
}
